import { contentWithToken } from "@/components/feedback/feedback_file_link";
import { html, TemplateResult } from "lit";
import { customElement, property } from "lit/decorators.js";
import { DodonaElement } from "@/components/meta/dodona_element";
import { I18n } from "@/i18n/i18n";
import "@/components/feedback/feedback_context_body";
import type { Context, DiffStyle, FeedbackEnvelopeContext } from "@/types/FeedbackResult";
import type { Scratchpad } from "@/components/scratchpad";

/**
 * Renders one context (group of testcases) within a tab.
 * Shows a collapsible header with the context description, optional stdin/statements,
 * and the list of `d-feedback-testcase` children.
 * The collapse state is owned by the parent tab: this component renders `collapsed`
 * and reports clicks on the chevron with a `toggle-collapse` event.
 *
 * @element d-feedback-context
 *
 * @prop {Context}                 context           - Context data (testcases, messages, …).
 * @prop {number}                  context-index     - Zero-based position within the tab (used for anchor IDs).
 * @prop {number}                  tab-index         - Index of the parent tab (used for anchor IDs).
 * @prop {boolean}                 collapsed         - Whether the testcases are hidden.
 * @prop {string}                  diff-style        - Forwarded to child testcases; "split" or "unified".
 * @prop {string|null}             code              - Submission source code (used by the tutor button).
 * @prop {FeedbackEnvelopeContext} submissionContext - Submission context (session keys, tutor, file viewer).
 *
 * @fires toggle-collapse - The user clicked the collapse chevron.
 */
@customElement("d-feedback-context")
export class FeedbackContext extends DodonaElement {
    @property({ type: Object }) context!: Context;
    /** 0-based index of this group within the tab */
    @property({ type: Number, attribute: "context-index" }) contextIndex: number = 0;
    /** 0-based tab index */
    @property({ type: Number, attribute: "tab-index" }) tabIdx: number = 0;
    @property({ type: Boolean }) collapsed: boolean = false;
    @property({ type: String }) diffStyle: DiffStyle = "split";
    @property({ type: String }) code: string | null = null;
    @property({ type: Object }) submissionContext: FeedbackEnvelopeContext = {};

    private get groupId(): string {
        return `tab-${this.tabIdx}-group-${this.contextIndex}`;
    }

    private _toggle(): void {
        this.dispatchEvent(new CustomEvent("toggle-collapse"));
    }

    private async _onTutorClick(): Promise<void> {
        const files: { inline: Record<string, string>; href: Record<string, string> } = { inline: {}, href: {} };

        // Include files provided on the context level.
        for (const [name, value] of Object.entries(this.context.data?.files ?? {})) {
            files[value.location][name] = contentWithToken(
                value.content,
                value.location,
                this.submissionContext.accessToken ?? null,
            );
        }

        // Include files provided on the testcase level (i.e. Pythia judge).
        for (const testcase of this.context.groups ?? []) {
            for (const [name, value] of Object.entries(testcase.data?.files ?? {})) {
                files[value.location][name] = contentWithToken(
                    value.content,
                    value.location,
                    this.submissionContext.accessToken ?? null,
                );
            }
        }

        // d-scratchpad loads Papyros only now.
        await document.querySelector<Scratchpad>("d-scratchpad").debugTest({
            code: this.code ?? "",
            context: this.context,
            number: this.contextIndex + 1,
            files,
            submissionContext: this.submissionContext,
            diffStyle: this.diffStyle,
        });
    }

    private _renderTutorLink(g: Context): TemplateResult | "" {
        // Rendered by Activities::ScratchpadComponent; importing its element would pull Papyros into this pack.
        if (!(g.data?.statements || g.data?.stdin) || !document.querySelector("d-scratchpad")) return "";

        return html`<button
            type="button"
            class="btn btn-text tutorlink"
            data-lit
            title="${I18n.t("js.submissions_show.start_debugger")}"
            @click=${this._onTutorClick}
        >
            <i class="mdi me-1 mdi-bug-play-outline" aria-hidden="true"></i>
            ${I18n.t("js.submissions_show.debug")}
        </button>`;
    }

    render(): TemplateResult {
        const g = this.context;
        if (!g) return html``;

        const statusClass = g.accepted ? "correct" : "wrong";
        const collapsedClass = this.collapsed ? "collapsed" : "";
        const groupNumber = this.contextIndex + 1;
        const toggleLabel = I18n.t("js.control_names.toggle_test_group", { number: groupNumber });
        const jumpLabel = I18n.t("js.control_names.go_to_test_group", { number: groupNumber });
        const correctLabel = I18n.t("js.submissions_show.correct_group");
        const wrongLabel = I18n.t("js.submissions_show.wrong_group");

        return html`
            <div class="group card ${statusClass} ${collapsedClass}" id="${this.groupId}">
                <div class="card-title card-title-colored-container">
                    <a href="#${this.groupId}" aria-label="${jumpLabel}">#${groupNumber}</a>
                    <span class="ms-2 me-2">·</span>
                    <span class="group-status">
                        ${
                            g.accepted
                                ? html`<i class="mdi mdi-check mdi-18" aria-hidden="true"></i>
                                      <span class="ms-1">${correctLabel}</span>`
                                : html`<i class="mdi-close mdi mdi-18" aria-hidden="true"></i>
                                      <span class="ms-1">${wrongLabel}</span>`
                        }
                    </span>
                    <div class="flex-spacer"></div>
                    ${this._renderTutorLink(g)}
                    <button
                        type="button"
                        class="btn btn-icon btn-collapse"
                        aria-label="${toggleLabel}"
                        aria-expanded="${!this.collapsed}"
                        @click=${this._toggle}
                    >
                        <i class="mdi mdi-chevron-down" aria-hidden="true"></i>
                    </button>
                </div>
                <div class="card-supporting-text">
                    <d-feedback-context-body
                        .context=${g}
                        .diffStyle=${this.diffStyle}
                        .submissionContext=${this.submissionContext}
                    ></d-feedback-context-body>
                </div>
            </div>
        `;
    }
}
